import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Icon,
  Accordion,
  Row,
  Col,
  Badge,
} from '@folio/stripes/components';
import { FaBook, FaWarehouse, FaBoxes, FaFile } from 'react-icons/fa';

const UniversalProgressTracker = ({
  // Progress data
  current,
  total,
  currentBatch,
  totalBatches,
  
  // Summary data
  summary,
  
  // Messages
  instanceMessages,
  holdingsMessages,
  itemsMessages,
  
  // Configuration
  mode,
  showDetailedStats,
  showBatchInfo,
  showAccordion,
  
  // Styling
  progressColor,
  progressHeight,
  showInlineText,
  
  // Context data for operations counter
  contextData,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    instance: true,
    holdings: true,
    items: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (total === 0) return 0;
    
    // Debug: Log if current exceeds total
    if (current > total) {
      console.warn(`Progress warning: current (${current}) exceeds total (${total})`);
    }
    
    const safeTotal = Math.max(total, 1);
    const safeCurrent = Math.min(current, total); // Ensure current doesn't exceed total
    const percentage = Math.round((safeCurrent / safeTotal) * 100);
    return Math.min(percentage, 100); // Cap at 100%
  };

  // Render progress bar
  const renderProgressBar = () => {
    // Only show progress bar if we have valid current/total values
    if (current === 0 && total === 0) return null;
    
    const percentage = getProgressPercentage();
    
    if (showInlineText) {
      // Edit mode style - with inline text
      return (
        <div style={{ margin: '20px 0' }}>
          <div
            style={{
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              height: progressHeight,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${percentage}%`,
                height: '100%',
                backgroundColor: progressColor,
                transition: 'width 0.6s ease',
                borderRadius: '4px',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: percentage > 50 ? '#fff' : '#333',
                fontSize: '0.8rem',
                fontWeight: 'bold',
              }}
            >
              {Math.min(current, total)}/{total} ({percentage}%)
            </div>
          </div>
          {showBatchInfo && totalBatches > 0 && (
            <div
              style={{
                textAlign: 'center',
                marginTop: '5px',
                fontSize: '0.8rem',
                color: '#666',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.progress.batch"
                values={{ currentBatch, totalBatches }}
                defaultMessage="Batch {currentBatch} of {totalBatches}"
              />
            </div>
          )}
        </div>
      );
    } else {
      // Create mode style - with external text
      return (
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              marginBottom: '8px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>
              <FormattedMessage
                id={`ui-plugin-inventory-templates.${mode}Progress.progress`}
                defaultMessage="Progress: {current} of {total}"
                values={{ current: Math.min(current, total), total }}
              />
            </span>
            <span>{percentage}%</span>
          </div>
          <div
            style={{
              width: '100%',
              height: progressHeight,
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${percentage}%`,
                height: '100%',
                backgroundColor: progressColor,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          {showBatchInfo && currentBatch > 0 && (
            <div
              style={{ marginTop: '4px', fontSize: '0.9rem', color: '#6c757d' }}
            >
              <FormattedMessage
                id={`ui-plugin-inventory-templates.${mode}Progress.batch`}
                defaultMessage="Processing batch {current}"
                values={{ current: currentBatch }}
              />
            </div>
          )}
        </div>
      );
    }
  };

  // Render summary statistics
  const renderSummaryStats = () => {
    if (mode === 'create') {
      // Create mode - simple stats
      return (
        <Row>
          {summary.map((summaryItem) => (
            <Col xs={4} key={summaryItem.category}>
              <div
                style={{
                  padding: '12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <div
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: '#28a745',
                  }}
                >
                  {summaryItem.new}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <FormattedMessage
                    id={`ui-plugin-inventory-templates.${mode}Progress.${summaryItem.category}.new`}
                    defaultMessage={`New ${summaryItem.category}`}
                  />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      );
    } else {
      // Edit mode - detailed stats
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
          {summary.map((summaryItem) => {
            const getIcon = (category) => {
              switch (category) {
                case 'instance': return <FaBook size={24} />;
                case 'holdings': return <FaWarehouse size={24} />;
                case 'items': return <FaBoxes size={24} />;
                default: return <FaFile size={24} />;
              }
            };
            
            // For Instance: show simple update status
            if (summaryItem.category === 'instance') {
              const hasUpdate = summaryItem.updated > 0;
              return (
                <div
                  key={summaryItem.category}
                  style={{
                    padding: '16px',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div style={{ color: '#6c757d', marginBottom: '8px' }}>
                    {getIcon(summaryItem.category)}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#6c757d', fontWeight: '500', marginBottom: '8px' }}>
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.templateView.templateSections.namespace.instance"
                      defaultMessage="Instance"
                    />
                  </div>
                  <div style={{ fontSize: '1rem', color: hasUpdate ? '#ffc107' : '#6c757d', fontWeight: 'bold' }}>
                    {hasUpdate ? (
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.summary.willBeUpdated"
                        defaultMessage="Will be updated"
                      />
                    ) : (
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.summary.noChanges"
                        defaultMessage="No changes"
                      />
                    )}
                  </div>
                </div>
              );
            }
            
            // For Holdings and Items: show detailed breakdown
            return (
              <div
                key={summaryItem.category}
                style={{
                  padding: '16px',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <div style={{ color: '#6c757d', marginBottom: '4px' }}>
                    {getIcon(summaryItem.category)}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#6c757d', fontWeight: '500' }}>
                    <FormattedMessage
                      id={`ui-plugin-inventory-templates.templateView.templateSections.namespace.${summaryItem.category}`}
                      defaultMessage={summaryItem.category?.charAt(0).toUpperCase() + summaryItem.category?.slice(1) || ''}
                    />
                  </div>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#28a745' }}>
                      {summaryItem.new}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 'bold' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.summary.table.create"
                        defaultMessage="Create"
                      />
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#ffc107' }}>
                      {summaryItem.updated}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 'bold' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.summary.table.update"
                        defaultMessage="Update"
                      />
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#dc3545' }}>
                      {summaryItem.deleted}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 'bold' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.summary.table.delete"
                        defaultMessage="Delete"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  // Render operations counter (for create mode)
  const renderOperationsCounter = () => {
    if (mode !== 'create' || !contextData) return null;

    const instanceCount = 1; // Always 1 for create
    const holdingsCount = contextData?.holdingsData?.length || 0;
    const itemsCount = Object.values(contextData?.itemsData || {}).reduce((total, items) => total + (items?.length || 0), 0);

    return (
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '12px', fontSize: '1rem', color: '#495057' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.operationsToPerform"
            defaultMessage="Operations to be performed:"
          />
        </h4>
        <Row>
          <Col xs={4}>
            <div
              style={{
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.2s ease',
              }}
            >
              <FaBook size={20} style={{ color: '#6c757d', marginBottom: '6px' }} />
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#495057',
                }}
              >
                {instanceCount}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#495057', fontWeight: 'bold' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createProgress.instance"
                  defaultMessage="Instance"
                />
              </div>
            </div>
          </Col>
          <Col xs={4}>
            <div
              style={{
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.2s ease',
              }}
            >
              <FaWarehouse size={20} style={{ color: '#6c757d', marginBottom: '6px' }} />
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#495057',
                }}
              >
                {holdingsCount}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#495057', fontWeight: 'bold' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createProgress.holdings"
                  defaultMessage="Holdings"
                />
              </div>
            </div>
          </Col>
          <Col xs={4}>
            <div
              style={{
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.2s ease',
              }}
            >
              <FaBoxes size={20} style={{ color: '#6c757d', marginBottom: '6px' }} />
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#495057',
                }}
              >
                {itemsCount}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#495057', fontWeight: 'bold' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createProgress.items"
                  defaultMessage="Items"
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  // Render message section with accordion (for create mode)
  const renderMessageSection = (title, messages, messageKey, isExpanded, toggleKey) => {
    const summaryItem = summary.find((p) => p.category === messageKey);
    const badgeType = summaryItem?.new > 0 ? 'success' : 'default';

    return (
      <Accordion
        id={`progress-${messageKey}`}
        label={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{title}</span>
            {summaryItem && (
              <Badge type={badgeType}>
                <FormattedMessage
                  id={`ui-plugin-inventory-templates.${mode}Progress.toCreate`}
                  defaultMessage="{count} to create"
                  values={{ count: summaryItem.new }}
                />
              </Badge>
            )}
          </div>
        }
        open={isExpanded}
        onToggle={() => toggleSection(toggleKey)}
      >
        <div
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            padding: '8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
          }}
        >
          {messages.length === 0 ? (
            <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
              <FormattedMessage
                id={`ui-plugin-inventory-templates.${mode}Progress.noMessages`}
                defaultMessage="No messages yet..."
              />
            </div>
          ) : (
            messages.map((msg, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                {msg}
              </div>
            ))
          )}
        </div>
      </Accordion>
    );
  };

  // Render messages directly (for edit mode)
  const renderDirectMessages = () => {
    return (
      <div style={{ maxHeight: '300px', overflow: 'auto', marginTop: '20px' }}>
        {/* Instance Messages */}
        {instanceMessages && instanceMessages.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h4>
              <FormattedMessage id="ui-plugin-inventory-templates.progress.instanceUpdates" />
            </h4>
            {instanceMessages.map((msg, idx) => (
              <div key={`instance-${idx}`} style={{ margin: '5px 0' }}>
                {msg}
              </div>
            ))}
          </div>
        )}

        {/* Holdings Messages */}
        {holdingsMessages && holdingsMessages.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h4>
              <FormattedMessage id="ui-plugin-inventory-templates.progress.holdingsUpdates" />
            </h4>
            {holdingsMessages.map((msg, idx) => (
              <div key={`holdings-${idx}`} style={{ margin: '5px 0' }}>
                {msg}
              </div>
            ))}
          </div>
        )}

        {/* Items Messages */}
        {itemsMessages && itemsMessages.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h4>
              <FormattedMessage id="ui-plugin-inventory-templates.progress.itemsUpdates" />
            </h4>
            {itemsMessages.map((msg, idx) => (
              <div key={`items-${idx}`} style={{ margin: '5px 0' }}>
                {msg}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Progress Bar - only show if we have progress data */}
      {(current > 0 || total > 0) && renderProgressBar()}

      {/* Summary Stats */}
      {showDetailedStats && (
        <div style={{ marginBottom: '20px' }}>
          {renderSummaryStats()}
        </div>
      )}

      {/* Operations Counter (Create mode only) */}
      {renderOperationsCounter()}

      {/* Messages */}
      {showAccordion ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {renderMessageSection(
            <FormattedMessage
              id={`ui-plugin-inventory-templates.${mode}Progress.instanceSection`}
              defaultMessage="Instance Creation"
            />,
            instanceMessages,
            'instance',
            expandedSections.instance,
            'instance',
          )}

          {renderMessageSection(
            <FormattedMessage
              id={`ui-plugin-inventory-templates.${mode}Progress.holdingsSection`}
              defaultMessage="Holdings Creation"
            />,
            holdingsMessages,
            'holdings',
            expandedSections.holdings,
            'holdings',
          )}

          {renderMessageSection(
            <FormattedMessage
              id={`ui-plugin-inventory-templates.${mode}Progress.itemsSection`}
              defaultMessage="Items Creation"
            />,
            itemsMessages,
            'items',
            expandedSections.items,
            'items',
          )}
        </div>
      ) : (
        renderDirectMessages()
      )}
    </div>
  );
};

UniversalProgressTracker.propTypes = {
  // Progress data
  current: PropTypes.number,
  total: PropTypes.number,
  currentBatch: PropTypes.number,
  totalBatches: PropTypes.number,
  
  // Summary data
  summary: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      new: PropTypes.number,
      updated: PropTypes.number,
      deleted: PropTypes.number,
    }),
  ),
  
  // Messages
  instanceMessages: PropTypes.array,
  holdingsMessages: PropTypes.array,
  itemsMessages: PropTypes.array,
  
  // Configuration
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  showDetailedStats: PropTypes.bool,
  showBatchInfo: PropTypes.bool,
  showAccordion: PropTypes.bool,
  
  // Styling
  progressColor: PropTypes.string,
  progressHeight: PropTypes.string,
  showInlineText: PropTypes.bool,
  
  // Context data
  contextData: PropTypes.object,
};

UniversalProgressTracker.defaultProps = {
  current: 0,
  total: 0,
  currentBatch: 0,
  totalBatches: 0,
  summary: [],
  instanceMessages: [],
  holdingsMessages: [],
  itemsMessages: [],
  showDetailedStats: true,
  showBatchInfo: true,
  showAccordion: false,
  progressColor: '#007bff',
  progressHeight: '20px',
  showInlineText: true,
  contextData: null,
};

export default UniversalProgressTracker;